import React from 'react';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
} from '@mui/material';

export interface StorageClearConfirmDialogProps {
  open: boolean;
  title: string;
  description: string;
  itemsToClear: string[];
  itemsToKeep?: string[];
  estimatedSpace?: string;
  requiresReload?: boolean;
  requiresReauth?: boolean;
  isClearing?: boolean;
  onConfirm: () => void | Promise<void>;
  onCancel: () => void;
}

export const StorageClearConfirmDialog: React.FC<StorageClearConfirmDialogProps> = ({
  open,
  title,
  description,
  itemsToClear,
  itemsToKeep = [],
  estimatedSpace,
  requiresReload = false,
  requiresReauth = false,
  isClearing = false,
  onConfirm,
  onCancel,
}) => {
  const handleConfirm = async () => {
    await onConfirm();
  };

  return (
    <Dialog open={open} onClose={isClearing ? undefined : onCancel} maxWidth="sm" fullWidth>
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        <DialogContentText>{description}</DialogContentText>

        {itemsToClear.length > 0 && (
          <Box sx={{ mt: 2 }}>
            <DialogContentText sx={{ fontWeight: 600, mb: 1 }}>This will clear:</DialogContentText>
            <List dense disablePadding>
              {itemsToClear.map((item, index) => (
                <ListItem key={index} disableGutters>
                  <ListItemIcon sx={{ minWidth: 32 }}>
                    <DeleteOutlineIcon fontSize="small" color="action" />
                  </ListItemIcon>
                  <ListItemText primary={item} primaryTypographyProps={{ variant: 'body2' }} />
                </ListItem>
              ))}
            </List>
          </Box>
        )}

        {itemsToKeep.length > 0 && (
          <Box sx={{ mt: 2 }}>
            <DialogContentText sx={{ fontWeight: 600, mb: 1, color: 'success.main' }}>
              This will keep:
            </DialogContentText>
            <List dense disablePadding>
              {itemsToKeep.map((item, index) => (
                <ListItem key={index} disableGutters>
                  <ListItemIcon sx={{ minWidth: 32 }}>
                    <CheckCircleOutlineIcon fontSize="small" color="success" />
                  </ListItemIcon>
                  <ListItemText
                    primary={item}
                    primaryTypographyProps={{ variant: 'body2', color: 'text.secondary' }}
                  />
                </ListItem>
              ))}
            </List>
          </Box>
        )}

        {estimatedSpace && (
          <Alert severity="info" sx={{ mt: 2 }}>
            Estimated space to be freed: {estimatedSpace}
          </Alert>
        )}

        {requiresReload && !requiresReauth && (
          <Alert severity="info" sx={{ mt: 2 }}>
            You&apos;ll need to refresh the page after this action
          </Alert>
        )}

        {requiresReauth && (
          <Alert severity="warning" sx={{ mt: 2 }}>
            You&apos;ll need to log in again after this action
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onCancel} disabled={isClearing}>
          Cancel
        </Button>
        <Button
          onClick={handleConfirm}
          color={requiresReauth ? 'error' : 'primary'}
          variant="contained"
          disabled={isClearing}
          startIcon={isClearing ? <CircularProgress size={16} /> : undefined}
        >
          {isClearing ? 'Clearing...' : requiresReauth ? 'Clear & Log Out' : 'Confirm'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
